import React, { Component } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { ModalInMobile, Button } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import { getSearchPageWindowFlag, updateSearchPageWindowFlag } from '../helper';
import css from './SearchFiltersMobile.module.css';

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    const searchParams = new URLSearchParams(props.location?.search);
    const isOpen = searchParams.get('mobilesearchfilter') === 'open';
    this.state = { isFiltersOpenOnMobile: isOpen, initialQueryParams: null };

    this.rootRef = null;
    this.scrollContainer = null;
    this.handleContainerScroll = null;

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.attachScrollListener = this.attachScrollListener.bind(this);
    this.detachScrollListener = this.detachScrollListener.bind(this);
    this.restoreMobileFilterScrollPosition = this.restoreMobileFilterScrollPosition.bind(this);
    this.getScrollContainer = this.getScrollContainer.bind(this);
  }

  componentDidMount() {
    this.restoreMobileFilterScrollPosition();
    this.attachScrollListener();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      const searchParams = new URLSearchParams(this.props.location.search);
      const isOpen = searchParams.get('mobilesearchfilter') === 'open';
      if (isOpen !== this.state.isFiltersOpenOnMobile) {
        this.setState({ isFiltersOpenOnMobile: isOpen });
      }
    }
    this.restoreMobileFilterScrollPosition();
    this.attachScrollListener();
  }

  componentWillUnmount() {
    this.detachScrollListener();
  }

  getScrollContainer() {
    if (this.rootRef) {
      return (
        this.rootRef.querySelector('[class*="scrollLayer"]') ||
        this.rootRef.querySelector('[class*="modalContainer"]') ||
        this.rootRef
      );
    }
    if (typeof document !== 'undefined') {
      return (
        document.querySelector('[class*="scrollLayer"]') ||
        document.querySelector('[class*="modalContainer"]')
      );
    }
    return null;
  }

  attachScrollListener() {
    if (this.state.isFiltersOpenOnMobile) {
      const el = this.getScrollContainer();
      if (el && !this.scrollContainer) {
        this.scrollContainer = el;
        this.handleContainerScroll = () => {
          const containerScroll = el.scrollTop || 0;
          const winScroll = typeof window !== 'undefined' ? window.scrollY || window.pageYOffset || 0 : 0;
          if (containerScroll > 0 || winScroll > 0) {
            updateSearchPageWindowFlag({
              mobileFilterScrollPos: containerScroll,
              mobileWindowScrollPos: winScroll,
            });
          }
        };
        el.addEventListener('scroll', this.handleContainerScroll, { passive: true });
        if (typeof window !== 'undefined') {
          window.addEventListener('scroll', this.handleContainerScroll, { passive: true });
        }
      }
    }
  }

  detachScrollListener() {
    if (this.scrollContainer && this.handleContainerScroll) {
      this.scrollContainer.removeEventListener('scroll', this.handleContainerScroll);
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', this.handleContainerScroll);
      }
      this.scrollContainer = null;
      this.handleContainerScroll = null;
    }
  }

  restoreMobileFilterScrollPosition() {
    const savedContainerScrollPos = getSearchPageWindowFlag('mobileFilterScrollPos');
    const savedWindowScrollPos = getSearchPageWindowFlag('mobileWindowScrollPos');

    if ((savedContainerScrollPos > 0 || savedWindowScrollPos > 0) && this.state.isFiltersOpenOnMobile) {
      const applyScroll = () => {
        const el = this.getScrollContainer();
        if (el && savedContainerScrollPos > 0) {
          el.scrollTop = savedContainerScrollPos;
        }
        if (savedWindowScrollPos > 0 && typeof window !== 'undefined') {
          window.scrollTo(0, savedWindowScrollPos);
        }
      };

      setTimeout(applyScroll, 0);
      setTimeout(applyScroll, 50);
      setTimeout(applyScroll, 150);
      setTimeout(applyScroll, 300);
    }
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams, history, location } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('mobilesearchfilter', 'open');
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, location, routeConfiguration } = this.props;
    updateSearchPageWindowFlag({ mobileFilterScrollPos: 0, mobileWindowScrollPos: 0 });

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    const cleanInitialQueryParams = { ...this.state.initialQueryParams };
    delete cleanInitialQueryParams.mobilesearchfilter;

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        cleanInitialQueryParams
      )
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    const { history, location, onCloseModal } = this.props;
    updateSearchPageWindowFlag({ mobileFilterScrollPos: 0, mobileWindowScrollPos: 0 });
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('mobilesearch');
    searchParams.delete('mobilesearchfilter');
    const cleanSearch = searchParams.toString();
    const newUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${location.hash ||
      ''}`;
    history.replace(newUrl);

    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      showAsModalMaxWidth,
      onMapIconClick = () => {},
      onManageDisableScrolling,
      selectedFiltersCount = 0,
      noResultsInfo,
      intl,
      isMapVariant = true,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.cancel' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    return (
      <div className={classes} ref={node => { this.rootRef = node; }}>
        {resultsCount !== 0 && (
          <>
            <div className={css.searchResultSummary}>
              {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
              {listingsAreLoaded && resultsCount === 0 ? noResults : null}
              {searchInProgress ? loadingResults : null}
            </div>
            <div className={css.buttons}>
              <PopupOpenerButton
                className={css.buttonDesign}
                isSelected={selectedFiltersCount > 0}
                toggleOpen={this.openFilters}
              >
                <FormattedMessage
                  id="SearchFiltersMobile.filtersButtonLabel"
                  className={css.mapIconText}
                />
              </PopupOpenerButton>

              {sortByComponent}
              {isMapVariant ? (
                <div className={css.mapIcon} onClick={onMapIconClick}>
                  <FormattedMessage
                    id="SearchFiltersMobile.openMapView"
                    className={css.mapIconText}
                  />
                </div>
              ) : null}
            </div>
            {noResultsInfo ? noResultsInfo : null}
          </>
        )}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>{filtersHeading}</span>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>{children}</div>
          ) : null}

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.urlQueryParams - The URL query params
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {number} props.showAsModalMaxWidth - The maximum width of the modal
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal
 * @param {Function} props.onCloseModal - The function to close the modal
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
